import * as React from 'react';

/**
 * Variant options for the Skeleton component
 * - text: Rounded corners (rounded-sm), height based on font size
 * - circular: Full round (rounded-full), equal width/height
 * - rectangular: Medium rounded corners (rounded-md)
 */
export type SkeletonVariant = 'text' | 'circular' | 'rectangular';

/**
 * Base props for the Skeleton component
 */
interface SkeletonBaseProps {
  /**
   * The shape variant of the skeleton.
   * - 'text': Inline text placeholder with slight rounding (rounded-sm)
   * - 'circular': Circle/avatar placeholder (rounded-full)
   * - 'rectangular': Block/card placeholder (rounded-md)
   * @default 'text'
   */
  variant?: SkeletonVariant;
  /**
   * Width of the skeleton.
   * - String values are used directly (e.g., "100%", "10rem")
   * - Number values are converted to pixels
   * @default '100%'
   */
  width?: string | number;
  /**
   * Height of the skeleton.
   * - String values are used directly (e.g., "1.5rem", "200px")
   * - Number values are converted to pixels
   * - For 'text' variant, defaults to '1em' (matches font size)
   * - For 'circular' variant, should match width for a perfect circle
   * @default '1em' for text, undefined for others
   */
  height?: string | number;
  /**
   * Whether to show the shimmer animation.
   * When false, displays a static skeleton.
   * @default true
   */
  animation?: boolean;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * SkeletonProps extends HTML span attributes
 */
export type SkeletonProps = SkeletonBaseProps &
  Omit<React.HTMLAttributes<HTMLSpanElement>, keyof SkeletonBaseProps>;

/**
 * Border radius classes for each variant
 */
const variantRadiusMap: Record<SkeletonVariant, string> = {
  text: 'rounded-sm',
  circular: 'rounded-full',
  rectangular: 'rounded-md',
};

/**
 * Convert dimension value to CSS string
 */
function toCssValue(value: string | number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

/**
 * Skeleton component for displaying loading placeholders.
 *
 * Designed for insurance industry applications where clear loading states
 * help users understand that content is being fetched. Skeletons provide
 * a better user experience than blank screens or simple spinners for
 * content-heavy interfaces.
 *
 * Features:
 * - Three shape variants: text, circular, rectangular
 * - Configurable dimensions (width/height)
 * - Smooth shimmer animation (1.5s)
 * - Respects prefers-reduced-motion preference
 * - Accessible: aria-hidden since it's decorative
 *
 * @example
 * // Basic text skeleton
 * <Skeleton />
 *
 * @example
 * // Different variants
 * <Skeleton variant="text" width="60%" />
 * <Skeleton variant="circular" width={40} height={40} />
 * <Skeleton variant="rectangular" width="100%" height={120} />
 *
 * @example
 * // Without animation
 * <Skeleton animation={false} />
 *
 * @example
 * // Card skeleton
 * <div className="space-y-4">
 *   <Skeleton variant="rectangular" height={200} />
 *   <Skeleton variant="text" width="80%" />
 *   <Skeleton variant="text" width="60%" />
 * </div>
 */
const Skeleton = React.forwardRef<HTMLSpanElement, SkeletonProps>(
  (
    {
      variant = 'text',
      width,
      height,
      animation = true,
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    // Determine default dimensions based on variant
    const defaultWidth = variant === 'circular' ? undefined : '100%';
    const defaultHeight = variant === 'text' ? '1em' : undefined;

    // Convert dimensions to CSS values
    const cssWidth = toCssValue(width) ?? toCssValue(defaultWidth);
    const cssHeight = toCssValue(height) ?? toCssValue(defaultHeight);

    // For circular variant, if only width or height is provided, use it for both
    /* v8 ignore start */
    const finalWidth = cssWidth ?? (variant === 'circular' ? cssHeight : undefined);
    /* v8 ignore stop */
    const finalHeight = cssHeight ?? (variant === 'circular' ? cssWidth : undefined);

    // Build class list
    const classes = [
      'inline-block',
      'bg-gradient-to-r',
      'from-neutral-200',
      'via-neutral-100',
      'to-neutral-200',
      'bg-[length:200%_100%]',
      variantRadiusMap[variant],
      animation ? 'animate-skeleton-shimmer motion-reduce:animate-none' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Build style object
    const computedStyle: React.CSSProperties = {
      ...style,
      width: finalWidth,
      height: finalHeight,
    };

    return (
      <span
        ref={ref}
        className={classes}
        style={computedStyle}
        aria-hidden="true"
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export { Skeleton };
